import React, { useState, useEffect } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Breadcrumb, Layout, PageHeader, Spin, Tag, Switch } from 'antd'

// Services
import { errorMessage, successMessage } from 'services/messager'
import {
	downloadAttachment,
	setResponsible,
	updateStatus,
	approveWorkflowStep,
	rejectWorkflowStep,
	archiveWorkflow,
	toggleEmailNotification,
	getDemandById
} from 'services/demands/demandsServices'
import { useSelector } from 'react-redux'

// Components
import MainLayout from 'components/mainLayout'
import DemandsButtons from 'components/demandsButtons'
import ConfirmationModal from 'components/ConfirmationModal'
import SetResponsibleModal from 'pages/demands/components/SetResponsibleModal'
import DemandsStep from './components/DemandsStep'
import CommentsSection from './components/CommentsSection'
import ResumeSidebar from './components/ResumeSidebar'
import Tab from './components/Tab'
import DemandsDetails from './components/DemandsDetails'
import DemandsTimeline from './components/DemandsTimeline'
import DemandsModal from './components/DemandsModal'

// Hooks
import useDemandWorkflow from './hooks/useDemandWorkflow'
import useDemandHistory from './hooks/useDemandHistory'

//icons
import TextSearch from '../../assets/icons/text-search.svg'
import Version from '../../assets/icons/version.svg'
import Timeline from '../../assets/icons/timeline.svg'
import DemandsVersionController from './components/DemandsVersionController'

import styles from './index.module.scss'

const statusColorMap = {
	'Aguardando ação': 'processing',
	'Em Validação': 'geekblue',
	'Em Revisão': 'cyan',
	'Em Assinatura': 'purple',
	Concluído: 'success',
	Atrasado: 'error',
	Cancelado: 'orange',
	Arquivado: 'gold',
}

const priorityColorMap = {
	Baixa: 'geekblue',
	Média: 'warning',
	Alta: 'volcano',
	Urgente: 'error',
}

const tabs = [
	{
		id: 'details',
		title: 'Detalhes',
		icon: <img className={styles.tabIcon} src={TextSearch} alt="Detalhes" />,
	},
	{
		id: 'version',
		title: 'Controle de versões',
		icon: (
			<img className={styles.tabIcon} src={Version} alt="Controle de versões" />
		),
	},
	{
		id: 'timeline',
		title: 'Histórico',
		icon: <img className={styles.tabIcon} src={Timeline} alt="Histórico" />,
	},
]

const demandSteps = [
	{ step_number: 1, name: 'Área solicitante' },
	{ step_number: 2, name: 'Área gestora' },
	{ step_number: 3, name: 'Área técnica' },
	{ step_number: 4, name: 'Diretoria' },
	{ step_number: 5, name: 'Área jurídica' },
	{ step_number: 6, name: 'Confirmação do solicitante' },
]

export default function DetailsDemands() {
	const { id } = useParams()
	const history = useHistory()
	const queryClient = useQueryClient()
	const user = useSelector(({ session }) => session)
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [modalType, setModalType] = useState('advance')
	const [currentStep, setCurrentStep] = useState()
	const [attachments, setAttachments] = useState([])
	const [isWorkflowTask, setIsWorkflowTask] = useState(false)
	const [isAuthorizedToEdit, setIsAuthorizedToEdit] = useState(false)
	const [notifyEmail, setNotifyEmail] = useState(false)

	const {
		data: workflowData,
		isLoading: isLoadingWorkFlow,
		isError: isErrorWorkFlow,
	} = useDemandWorkflow(id)

	const { data: historyData } = useDemandHistory(id)

	const { data: demandData } = useQuery({
		queryKey: ['demand', id],
		queryFn: () => getDemandById(id),
		enabled: !!id
	})

	const hasLegalAccess = ['legal', 'admin'].includes(user?.perfil)

	const taskNameLabel =
		user.company_id === 96
			? 'Título do chamado'
			: 'Nome do fornecedor/contraparte'

	const [openModal, setOpenModal] = useState(undefined)
	const [currentTab, setCurrentTab] = useState(tabs[0].id)

	const generalInfoItems = [
		{ value: workflowData?.task?.category?.name, label: 'Categoria' },
		{ value: workflowData?.task?.requester?.name, label: 'Solicitante' },
		{
			value: workflowData?.task?.solicitantArea?.name || 'Não informada',
			label: 'Área Solicitante',
		},
		{
			value: workflowData?.task?.requester?.email,
			label: 'E-mail do solicitante',
		},
		...(workflowData?.task?.technicalArea
			? [
					{
						value: workflowData?.task?.technicalArea || 'Não informada',
						label: 'Área Técnica',
					},
				]
			: []),
		...(workflowData?.task?.document_template_id
			? [
					{
						value: workflowData?.task?.model?.name,
						label: 'Modelo',
					},
				]
			: []),
		...(workflowData?.task?.custom_fields
			? Object.entries(workflowData.task.custom_fields)
					.filter(
						([key, field]) =>
							key !== 'resume' && field.label !== 'Resumo da demanda'
					)
					.map(([, field]) => ({
						value: field.value,
						label: field.label,
					}))
			: []),
	]

	const resumeField = workflowData?.task?.custom_fields
		? Object.entries(workflowData.task.custom_fields).find(
				([key, field]) => key === 'resume' || field.label === 'Resumo da demanda'
			)?.[1]
		: null

	const legalInfoItems = [
		{
			value: workflowData?.task?.responsible?.name,
			label: 'Responsável jurídico designado',
		},
		{
			value: new Date(workflowData?.task?.deadline).toLocaleDateString('pt-BR'),
			label: 'Prazo de conclusão',
		},
		...(workflowData?.task?.document_id
			? [
					{
						value: workflowData?.task?.name,
						label: 'Documento associado',
						isButton: true,
						onClick: () => {
							history.push(`/documents/${workflowData?.task.document_id}`)
						},
					},
				]
			: []),
	]

	const { mutate: handleDownloadAttachment, isPending: isDownloading } =
		useMutation({
			mutationFn: async (attachment) => {
				const data = await downloadAttachment(attachment.id)

				return data.fileUrl
			},
			onSuccess: (fileUrl) => {
				window.open(fileUrl, '_blank', 'noopener,noreferrer')
				successMessage({
					content: 'Download do anexo feito com sucesso!',
				})
			},
			onError: (error) => {
				console.error(error)
				errorMessage({
					content: 'Falha durante o download do anexo.',
				})
			},
		})

	const { mutate: changeDemandStatus, isPending: isLoadingChangeStatus } =
		useMutation({
			mutationFn: async ({ status }) => {
				const data = await updateStatus({ id, status })
				return data
			},
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['demands'] })
				queryClient.invalidateQueries({ queryKey: ['demand', id] })
				setOpenModal(undefined)
				successMessage({
					content: 'Status da demanda alterado com sucesso!',
				})
			},
			onError: () => {
				errorMessage({
					content: 'Erro ao alterar status da demanda.',
				})
			},
		})

	const { mutate: handleSetResponsible, isPending: isLoadingSetResponsible } =
		useMutation({
			mutationFn: async (values) => {
				await setResponsible({
					taskId: id,
					userId: values.responsible,
				})
				return values.responsibleUser
			},
			onSuccess: (responsibleUser) => {
				if (responsibleUser) {
					queryClient.setQueryData(['demand-workflow', id], (old) =>
						old?.task
							? { ...old, task: { ...old.task, responsible: responsibleUser } }
							: old
					)
				} else {
					queryClient.invalidateQueries({ queryKey: ['demand-workflow', id] })
				}
				queryClient.invalidateQueries({ queryKey: ['demands'] })
				queryClient.invalidateQueries({ queryKey: ['demand', id] })
				setOpenModal(undefined)
				successMessage({ content: 'Responsável atribuido com sucesso!' })
			},
			onError: (error) => {
				console.error(error)
				errorMessage({
					content: 'Erro ao atribuir responsável.',
				})
			},
		})

	const { mutate: handleApproveStep, isPending: isLoadingApproveStep } =
		useMutation({
			mutationFn: async (values) => {
				const formData = new FormData()
				formData.append('taskId', id)
				formData.append('comments', values.justification ?? '')

				if (
					workflowData?.task?.current_workflow_step === 2 &&
					values.optionalRoute
				) {
					formData.append('optionalRoute', values.optionalRoute)
					formData.append('technicalAreaId', values.technicalAreaId)
				}

				if (values.files && values.files.length > 0) {
					values.files.forEach((file) => {
						formData.append('files', file.originFileObj)
					})
				}

				return await approveWorkflowStep(formData)
			},
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['demand-workflow', id] })
				queryClient.invalidateQueries({ queryKey: ['demand-history', id] })
				queryClient.invalidateQueries({ queryKey: ['demand', id] })
				setIsModalOpen(false)
				successMessage({ content: 'Etapa aprovada com sucesso!' })
			},
			onError: (error) => {
				errorMessage({ content: error.message || 'Erro ao aprovar etapa.' })
			},
		})

	const { mutate: handleArchive, isPending: isLoadingArchive } = useMutation({
		mutationFn: async (values) => {
			const body = {
				taskId: id,
				comments: values.justification,
			}

			return await archiveWorkflow(body)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['demand-workflow', id] })
			queryClient.invalidateQueries({ queryKey: ['demand-history', id] })
			queryClient.invalidateQueries({ queryKey: ['demand', id] })
			setIsModalOpen(false)
			successMessage({ content: 'Etapa arquivada com sucesso!' })
		},
		onError: (error) => {
			errorMessage({ content: error.message || 'Erro ao arquivar etapa.' })
		},
	})

	const { mutate: handleRejectStep, isPending: isLoadingRejectStep } =
		useMutation({
			mutationFn: async (values) => {
				return await rejectWorkflowStep({
					taskId: id,
					targetStep: values.returnArea,
					comments: values.justification,
					...(workflowData?.task?.current_workflow_step === 5 &&
					values.optionalRoute
						? { optionalRoute: values.optionalRoute }
						: {}),
				})
			},
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['demand-workflow', id] })
				queryClient.invalidateQueries({ queryKey: ['demand-history', id] })
				queryClient.invalidateQueries({ queryKey: ['demand', id] })
				setIsModalOpen(false)
				successMessage({ content: 'Etapa devolvida com sucesso!' })
			},
			onError: (error) => {
				errorMessage({ content: error.message || 'Erro ao devolver etapa.' })
			},
		})

	const { mutate: handleToggleNotification, isPending: isLoadingToggle } = useMutation({
		mutationFn: async ({ checked }) => {
			setNotifyEmail(checked)
			await toggleEmailNotification(id, checked)
			return checked
		},
		onSuccess: (checked) => {
			successMessage({ 
				content: checked ? 'Notificações por email ativadas!' : 'Notificações por email desativadas.' 
			})
			queryClient.invalidateQueries({ queryKey: ['demand', id] })
		},
		onError: (error, variables) => {
			// Reverte o Switch se a API falhar
			setNotifyEmail(!variables.checked)
			console.error('Erro ao alterar notificação:', error)
			errorMessage({ content: 'Erro ao atualizar preferência de notificação.' })
		}
	})

	function handleFinishDemand() {
		changeDemandStatus({ status: 'Concluído' })
	}

	function handleCancelDemand() {
		changeDemandStatus({ status: 'Cancelado' })
	}

	function handleCloseModal() {
		setOpenModal(undefined)
	}

	function handleOpenModal(modal) {
		setOpenModal(modal)
	}

	function handleOpenUpdateModal(type) {
		setModalType(type)
		setIsModalOpen(true)
	}

	const handleGoBack = () => {
		history.goBack()
	}

	const handleTabClick = (tabId) => {
		setCurrentTab(tabId)
	}

	function checkIsAuthorizedToEdit(findedStep) {
		if (!findedStep || !workflowData || !user) return false
		const {
			gestoraTeamId,
			juridicaTeamId,
			technicalAreaTeamId,
			directoriaTeamId,
			route,
		} = workflowData?.task?.workflow_meta || {}
		const currentStepNumber = findedStep.step_number || 1
		const userTeamIds = user?.user_teams?.map((t) => t.team.id) || []

		if (user.perfil === 'admin') return setIsAuthorizedToEdit(true)

		switch (currentStepNumber) {
			case 1:
			case 6:
				return setIsAuthorizedToEdit(
					user?.id === workflowData.task.requested_by
				)
			case 2:
				return setIsAuthorizedToEdit(userTeamIds.includes(gestoraTeamId))
			case 3: {
				if (!route || route === 'nenhuma' || route === 'diretoria')
					return setIsAuthorizedToEdit(false)
				return setIsAuthorizedToEdit(userTeamIds.includes(technicalAreaTeamId))
			}
			case 4: {
				if (!route || route === 'nenhuma' || route === 'area_tecnica')
					return setIsAuthorizedToEdit(false)
				return setIsAuthorizedToEdit(userTeamIds.includes(directoriaTeamId))
			}
			case 5:
				return setIsAuthorizedToEdit(
					user.perfil === 'legal' && userTeamIds.includes(juridicaTeamId)
				)
			default:
				return setIsAuthorizedToEdit(false)
		}
	}

	useEffect(() => {
		if (!workflowData) return
		const currentAttachments = workflowData.attachments
		setAttachments(currentAttachments)
		const findedStep = workflowData.steps.find(
			(step) => step.step_number === workflowData.task.current_workflow_step
		)
		setCurrentStep(findedStep)
		checkIsAuthorizedToEdit(findedStep)
		setIsWorkflowTask(workflowData?.task?.category?.has_workflow)
	}, [workflowData, user])

	useEffect(() => {
		if (demandData) {
			setNotifyEmail(demandData?.notifyOnNewChatMessage ?? demandData?.task?.notifyOnNewChatMessage ?? false)
		}
	}, [demandData])

	if (isLoadingWorkFlow) {
		return (
			<MainLayout>
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						height: '100%',
						width: '100%',
					}}>
					<Spin size="large" />
				</div>
			</MainLayout>
		)
	}

	if (isErrorWorkFlow) {
		return (
			<MainLayout>
				<div>
					Não foi possível carregar os dados. Tente novamente mais tarde.
				</div>
			</MainLayout>
		)
	}

	if (!workflowData?.task) {
		return (
			<MainLayout>
				<div>Demanda não encontrada. Retorne à lista de demandas.</div>
			</MainLayout>
		)
	}

	return (
		<MainLayout selectedKey="demands.list">
			<Layout style={{ backgroundColor: '#fff' }}>
				<PageHeader
					extra={[
						<div key="notify-toggle" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<span>Receber notificações por email</span>
							<Switch
								checked={notifyEmail}
								onChange={(checked) => handleToggleNotification({ checked })}
								loading={isLoadingToggle}
							/>
						</div>
					]}
				>
					<Breadcrumb>
						<Breadcrumb.Item
							onClick={handleGoBack}
							style={{ cursor: 'pointer' }}>
							Demandas
						</Breadcrumb.Item>
						<Breadcrumb.Item>{workflowData?.task.name}</Breadcrumb.Item>
					</Breadcrumb>
					<DemandsButtons
						id={id}
						onOpenModal={handleOpenModal}
						loading={isLoadingChangeStatus}
						hasResponsible={!!workflowData?.task.responsible}
						hasWorkflow={!!workflowData?.task.current_workflow_step}
					/>
				</PageHeader>
				<Layout style={{ backgroundColor: '#fff' }}>
					<ConfirmationModal
						show={openModal === 'cancel'}
						title={'Cancelar demanda'}
						text={'Deseja realmente cancelar esta demanda?'}
						onCancel={handleCloseModal}
						cancelText="Fechar"
						onConfirm={handleCancelDemand}
						loading={isLoadingChangeStatus}
					/>
					<ConfirmationModal
						show={openModal === 'finish'}
						title={'Concluir demanda'}
						text={'Deseja marcar a demanda como concluída?'}
						onCancel={handleCloseModal}
						onConfirm={handleFinishDemand}
						loading={isLoadingChangeStatus}
					/>
					<SetResponsibleModal
						show={openModal === 'set-responsible'}
						onCancel={handleCloseModal}
						onConfirm={handleSetResponsible}
						loading={isLoadingSetResponsible}
						isLegalUser={user?.perfil === 'legal'}
						teamId={ workflowData?.task?.workflow_meta?.juridicaTeamId || workflowData?.task?.team_id}
					/>
					<div className={styles.container}>
						<header className={styles.header}>
							<div>
								<h1 className={styles.title}>{workflowData?.task.name}</h1>
								<p className={styles.subtitle}>{taskNameLabel}:</p>
							</div>
							<aside className={styles.asideContainer}>
								<div>
									<h4 className={styles.asideTitle}>Status</h4>
									<Tag
										color={statusColorMap[workflowData?.task.status] || 'gray'}>
										{workflowData?.task.status}
									</Tag>
								</div>
								<div>
									<h4 className={styles.asideTitle}>Prioridade</h4>
									<Tag
										color={
											priorityColorMap[workflowData?.task.priority] || 'gray'
										}>
										{workflowData?.task.priority}
									</Tag>
								</div>
							</aside>
						</header>

						{isWorkflowTask && (
							<div className={styles.demandsTabsContainer}>
								{tabs.map((tab) => (
									<Tab
										key={tab.id}
										tab={tab}
										action={handleTabClick}
										currentTab={currentTab}
									/>
								))}
							</div>
						)}

						{isWorkflowTask && (
							<section className={styles.demandsStepsContainer}>
								{demandSteps.map((step, index) => (
									<DemandsStep
										key={index}
										step={step}
										currentStepNumber={currentStep?.step_number}
									/>
								))}
							</section>
						)}

						<div className={styles.dataContent}>
							<div className={styles.mainContent}>
								{currentTab === 'details' && (
									<DemandsDetails
										data={workflowData?.task}
										attachments={attachments}
										isLoadingAttachments={isLoadingWorkFlow}
										isErrorAttachments={isErrorWorkFlow}
										handleDownloadAttachment={handleDownloadAttachment}
										isDownloading={isDownloading}
										hasLegalAccess={hasLegalAccess}
										generalInfoItems={generalInfoItems}
										legalInfoItems={legalInfoItems}
										resumeField={resumeField}
									/>
								)}

								{currentTab === 'version' && workflowData?.task && (
									<DemandsVersionController
										attachments={workflowData?.attachments || []}
									/>
								)}

								{currentTab === 'timeline' && workflowData?.task && (
									<DemandsTimeline
										timeline={historyData?.history || []}
										showExpandButton={false}
									/>
								)}

								<CommentsSection
									comments={workflowData?.comments || []}
									taskId={id}
									canAddComment={isAuthorizedToEdit}
								/>

								{isAuthorizedToEdit &&
									workflowData?.task &&
									!['Concluído', 'Arquivado'].includes(
										workflowData?.task?.status
									) && (
										<footer className={styles.actionsFooter}>
											{workflowData?.task?.current_workflow_step !== 1 && (
												<button
													type="button"
													className={styles.btnOutline}
													onClick={() => handleOpenUpdateModal('previousStep')}>
													Devolver para ajustes
												</button>
											)}
											{(workflowData?.task?.current_workflow_step === 1 ||
												workflowData?.task?.current_workflow_step === 5) && (
												<button
													type="button"
													className={`${styles.btnSolid} ${styles.btnReprovar}`}
													onClick={() => handleOpenUpdateModal('archive')}>
													Encerrar
												</button>
											)}
											<button
												type="button"
												className={`${styles.btnSolid} ${styles.btnAprovar}`}
												onClick={() => handleOpenUpdateModal('approve')}>
												Aprovar
											</button>
										</footer>
									)}
							</div>
							{isWorkflowTask && (
								<ResumeSidebar
									timeline={historyData?.history || []}
									currentStep={workflowData?.task?.current_workflow_step}
									attachments={workflowData?.attachments || []}
									createdAt={workflowData?.task?.created_at}
								/>
							)}
						</div>
					</div>
				</Layout>
			</Layout>
			<DemandsModal
				show={isModalOpen}
				type={modalType}
				onCancel={() => setIsModalOpen(false)}
				onConfirm={(values) => {
					if (modalType === 'approve') {
						handleApproveStep(values)
					} else if (modalType === 'archive') {
						handleArchive(values)
					} else if (modalType === 'previousStep') {
						handleRejectStep(values)
					} else {
						console.log('modalType inválido')
						return
					}
				}}
				loading={isLoadingApproveStep || isLoadingRejectStep}
				workflowData={workflowData}
			/>
		</MainLayout>
	)
}